import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { navigationItems, siteData } from "../siteData";

function MetaController({ title, description }) {
  useEffect(() => {
    document.title = title ? `${title} | ${siteData.companyName}` : siteData.companyName;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description || siteData.description);
  }, [description, title]);

  return null;
}

function ProjectsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === "/projects" || location.pathname === "/rnd";

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`nav-link flex items-center gap-1 ${isActive ? "active" : ""}`}
      >
        Projects
        <svg className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-48 rounded-2xl border border-emerald-900/10 bg-white py-2 shadow-lg z-50">
          <NavLink
            to="/projects"
            onClick={() => setOpen(false)}
            className={({ isActive }) => `block px-4 py-2.5 text-sm font-medium transition hover:bg-emerald-50 hover:text-emerald-900 ${isActive ? "text-emerald-700" : "text-slate-700"}`}
          >
            All Projects
          </NavLink>
          <NavLink
            to="/rnd"
            onClick={() => setOpen(false)}
            className={({ isActive }) => `block px-4 py-2.5 text-sm font-medium transition hover:bg-emerald-50 hover:text-emerald-900 ${isActive ? "text-emerald-700" : "text-slate-700"}`}
          >
            R&D Works
          </NavLink>
        </div>
      )}
    </div>
  );
}

function ServicesDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === "/services" || location.pathname === "/callentica";

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`nav-link flex items-center gap-1 ${isActive ? "active" : ""}`}
      >
        Services
        <svg className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-56 rounded-2xl border border-emerald-900/10 bg-white py-2 shadow-lg z-50">
          <NavLink
            to="/services"
            onClick={() => setOpen(false)}
            className={({ isActive }) => `block px-4 py-2.5 text-sm font-medium transition hover:bg-emerald-50 hover:text-emerald-900 ${isActive ? "text-emerald-700" : "text-slate-700"}`}
          >
            All Services
          </NavLink>
          <NavLink
            to="/callentica"
            onClick={() => setOpen(false)}
            className={({ isActive }) => `block px-4 py-2.5 text-sm font-medium transition hover:bg-emerald-50 hover:text-emerald-900 ${isActive ? "text-emerald-700" : "text-slate-700"}`}
          >
            Callentica – AI Voice Assistant
          </NavLink>
        </div>
      )}
    </div>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-emerald-900/10 bg-white/85 backdrop-blur-xl">
      <div className="site-shell flex items-center justify-between gap-6 py-4">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/chip5.png"
            alt="SaskTech Lab"
            className="h-14 w-auto"
          />
          <span className="flex flex-col leading-tight">
            <span className="font-display text-2xl font-bold">
              <span style={{ color: "#1a2e6e" }}>SaskTech </span>
              <span style={{ color: "#3a8a3a" }}>Lab</span>
            </span>
            <span className="text-xs tracking-widest uppercase" style={{ color: "#3a8a3a" }}>Innovate, Educate, Elevate</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {navigationItems.map((item) =>
            item.label === "Services" ? (
              <ServicesDropdown key="services" />
            ) : item.label === "Projects" ? (
              <ProjectsDropdown key="projects" />
            ) : (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <Link to={siteData.primaryCta.to} className="button-tertiary">
            {siteData.primaryCta.label}
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-emerald-900/10 bg-white text-emerald-950 lg:hidden"
          onClick={() => setMobileOpen((open) => !open)}
          aria-label="Toggle navigation"
        >
          <span className="text-xl">☰</span>
        </button>
      </div>

      {mobileOpen ? (
        <div className="border-t border-emerald-900/10 bg-white lg:hidden">
          <div className="site-shell flex flex-col gap-4 py-4">
            {navigationItems.map((item) =>
              item.label === "Services" ? (
                <React.Fragment key="services-mobile">
                  <NavLink to="/services" className="nav-link" onClick={() => setMobileOpen(false)}>
                    Services
                  </NavLink>
                  <NavLink to="/callentica" className={({ isActive }) => `nav-link pl-4 text-sm text-slate-500 ${isActive ? "active" : ""}`} onClick={() => setMobileOpen(false)}>
                    ↳ Callentica – AI Voice Assistant
                  </NavLink>
                </React.Fragment>
              ) : item.label === "Projects" ? (
                <React.Fragment key="projects-mobile">
                  <NavLink to="/projects" className="nav-link" onClick={() => setMobileOpen(false)}>
                    Projects
                  </NavLink>
                  <NavLink to="/rnd" className={({ isActive }) => `nav-link pl-4 text-sm text-slate-500 ${isActive ? "active" : ""}`} onClick={() => setMobileOpen(false)}>
                    ↳ R&D Works
                  </NavLink>
                </React.Fragment>
              ) : (
                <NavLink key={item.to} to={item.to} className="nav-link" onClick={() => setMobileOpen(false)}>
                  {item.label}
                </NavLink>
              )
            )}
            <Link to={siteData.primaryCta.to} className="button-tertiary" onClick={() => setMobileOpen(false)}>
              {siteData.primaryCta.label}
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}

function Footer() {
  const quickLinks = [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Projects", to: "/projects" },
    { label: "Contact", to: "/contact" },
  ];

  const serviceLinks = [
    { label: "Software Engineering", to: "/services" },
    { label: "Hardware Engineering", to: "/services" },
    { label: "IT Solutions", to: "/services" },
    { label: "IoT Integration", to: "/services" },
    { label: "Prototyping", to: "/services" },
  ];

  return (
    <footer className="mt-24 bg-[#1a1a2e]">
      <div className="site-shell grid gap-10 py-14 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3">
            <img src="/chip5.png" alt="SaskTech Lab" className="h-10 w-auto brightness-0 invert" />
            <p className="font-display text-xl font-bold text-white">{siteData.companyName}</p>
          </div>
          <p className="mt-4 text-sm leading-7 text-slate-400">
            Engineering solutions and talent development for modern industries.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <p className="font-display text-sm font-semibold uppercase tracking-widest text-slate-300">Quick Links</p>
          <ul className="mt-4 space-y-2.5">
            {quickLinks.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="text-sm text-slate-400 transition hover:text-emerald-400">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div>
          <p className="font-display text-sm font-semibold uppercase tracking-widest text-slate-300">Services</p>
          <ul className="mt-4 space-y-2.5">
            {serviceLinks.map((item) => (
              <li key={item.label}>
                <Link to={item.to} className="text-sm text-slate-400 transition hover:text-emerald-400">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <p className="font-display text-sm font-semibold uppercase tracking-widest text-slate-300">Contact Us</p>
          <ul className="mt-4 space-y-3 text-sm text-slate-400">
            {siteData.addressLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
            <li>
              <a href={`mailto:${siteData.email}`} className="transition hover:text-emerald-400">
                Email: {siteData.email}
              </a>
            </li>
            <li>
              <a href={siteData.phoneHref} className="transition hover:text-emerald-400">
                Phone: {siteData.phone}
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-white/10">
        <div className="site-shell py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {siteData.companyName}. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl">
      {eyebrow ? <p className="section-tag">{eyebrow}</p> : null}
      <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-emerald-950 md:text-5xl">{title}</h2>
      {description ? <p className="mt-5 text-base leading-8 text-slate-600">{description}</p> : null}
    </div>
  );
}

export function PageHero({ eyebrow, title, description, actions = [] }) {
  return (
    <section className="hero-shell-full mt-6 mb-12">
      <div className="site-shell relative z-10 flex flex-col items-center pt-20 pb-28 text-center">
        {eyebrow ? (
          <p className="inline-block rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-emerald-100 backdrop-blur-sm">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="mt-6 max-w-4xl font-display text-5xl font-bold leading-tight text-white md:text-7xl">{title}</h1>
        <p className="mt-6 max-w-2xl text-lg leading-8 text-emerald-50/90">{description}</p>
        {actions.length ? <div className="mt-10 flex flex-wrap justify-center gap-4">{actions}</div> : null}
      </div>
    </section>
  );
}

export function SiteLayout({ title, description, children }) {
  return (
    <div className="min-h-screen pb-10">
      <MetaController title={title} description={description} />
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}